import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import {
  Trophy, TrendingUp, Target, DollarSign, Users,
  Medal, Crown, Star, ChevronUp, ChevronDown
} from "lucide-react";
import { API } from "../../apiConfig";


const SalesLeaderboard = ({ token }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales-agent/leaderboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200";
    if (rank === 2) return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200";
    if (rank === 3) return "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200";
    return "bg-card";
  };

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Sales Leaderboard
          </CardTitle>
          <div className="flex gap-1">
            {["week", "month", "quarter"].map((p) => (
              <Button
                key={p}
                variant={period === p ? "default" : "ghost"}
                size="sm"
                onClick={() => setPeriod(p)}
                className="text-xs"
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No data yet</div>
        ) : (
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map((agent, index) => (
              <div
                key={agent.agent_id}
                className={`flex items-center gap-4 p-3 rounded-xl border ${getRankBg(agent.rank)}`}
              >
                <div className="flex items-center justify-center w-8">
                  {getRankIcon(agent.rank)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{agent.name}</p>
                  <p className="text-xs text-muted-foreground">{agent.territory || "All Areas"}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">{formatPrice(agent.period_sales)}</p>
                  <p className="text-xs text-muted-foreground">{agent.linked_accounts} accounts</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesLeaderboard;
